import { currentSeason } from './rotationScore.js'
import { localDayISO } from './localDay.js'
import { arrayValue } from './homeNav.js'
import {
  complimentCardStats,
  durationCardStats,
  lastWornCardStats,
  wearCardStats,
} from './fragranceCardStats.js'
import { isGymWear } from './wearOccasions.js'
import { complimentCountForLog } from './complimentEvents.js'
import { preferenceIdentity } from './productIntegration.js'

// §3.6 tile taxonomy. Every tile is computed straight from wear_logs +
// the owned collection — no new columns/tables. A tile is omitted entirely
// (not shown empty) until it has at least one qualifying wear, per the
// "reveal-on-first-data" rule: new users see nothing here until they've
// earned it, framed as a reveal rather than an incompleteness checklist.

function timestampForLog(log) {
  const applications = log?.applications || []
  const latest = applications.reduce((acc, app) => {
    const t = new Date(app.applied_at || 0).getTime()
    return t > acc ? t : acc
  }, 0)
  return latest || new Date(log?.created_at || `${log?.worn_on || log?.wear_date || '1970-01-01'}T12:00:00`).getTime()
}

export function rankFragrancesByWearCount(logs, predicate = () => true) {
  const counts = new Map()
  logs.filter(predicate).forEach(log => {
    if (!log.fragrance_id) return
    counts.set(log.fragrance_id, (counts.get(log.fragrance_id) || 0) + 1)
  })
  return [...counts.entries()]
    .map(([fragranceId, count]) => ({ fragranceId, count }))
    // Array.sort is stable: equal counts retain the existing first-wear order.
    .sort((a, b) => b.count - a.count)
}

function topFragranceByWearCount(logs, predicate) {
  return rankFragrancesByWearCount(logs, predicate)[0] || null
}

function relativeDayLabel(log) {
  const dateStr = log?.worn_on || log?.wear_date
  if (!dateStr) return ''
  const today = localDayISO()
  if (dateStr === today) return 'Today'
  const diffDays = Math.round((new Date(`${today}T00:00:00`) - new Date(`${dateStr}T00:00:00`)) / 86400000)
  if (diffDays === 1) return 'Yesterday'
  if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago`
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Home's compact deck is a ranked list of fragrances, not a second copy of
// the category rail used on Log. Up to three compliment leaders lead, then
// distinct recent non-Gym wears fill the remaining slots through six.
export function buildHomeReferenceDeck(logs = [], fragrances = [], limit = 6) {
  const fragranceForId = id => fragrances.find(fragrance =>
    fragrance.id === id || (fragrance.sizes || []).some(size => size.id === id)) || null
  const nonGymLogs = logs.filter(log => !isGymWear(log) && log.fragrance_id)
  const complimentTotals = new Map()

  nonGymLogs.forEach(log => {
    const fragrance = fragranceForId(log.fragrance_id)
    const count = complimentCountForLog(log)
    if (!fragrance || count <= 0) return
    const identity = preferenceIdentity(fragrance)
    const current = complimentTotals.get(identity) || { count: 0, fragrance }
    current.count += count
    complimentTotals.set(identity, current)
  })

  const complimented = [...complimentTotals.entries()]
    .sort(([, a], [, b]) => b.count - a.count)
    .slice(0, 3)
  const used = new Set(complimented.map(([identity]) => identity))
  const tiles = complimented.map(([identity, entry], index) => ({
    key: `complimented:${identity}`,
    label: index === 0 ? 'Most Complimented' : 'Complimented',
    fragrance: entry.fragrance,
    ...complimentCardStats(entry.count),
  }))

  const recentByIdentity = new Map()
  nonGymLogs.slice().sort((a, b) => timestampForLog(b) - timestampForLog(a)).forEach(log => {
    const fragrance = fragranceForId(log.fragrance_id)
    if (!fragrance) return
    const identity = preferenceIdentity(fragrance)
    if (!recentByIdentity.has(identity)) recentByIdentity.set(identity, { fragrance, log })
  })

  for (const [identity, entry] of recentByIdentity) {
    if (tiles.length >= limit) break
    if (used.has(identity)) continue
    used.add(identity)
    tiles.push({
      key: `recent:${identity}`,
      label: 'Recently Worn',
      fragrance: entry.fragrance,
      ...lastWornCardStats(relativeDayLabel(entry.log)),
    })
  }

  return tiles.slice(0, limit)
}

export function buildTileTaxonomy(logs = [], fragrances = [], { durationRanking = null } = {}) {
  const byId = id => fragrances.find(f => f.id === id) || null
  const tiles = []

  const signature = topFragranceByWearCount(
    logs,
    log => (log.wear_label || 'sotd') === 'sotd' && !isGymWear(log),
  )
  if (signature) {
    tiles.push({ key: 'signature', label: 'Signature Scent', fragrance: byId(signature.fragranceId), ...wearCardStats(signature.count) })
  }

  const gym = topFragranceByWearCount(logs, isGymWear)
  if (gym) {
    tiles.push({ key: 'gym', label: 'Gym Scent', fragrance: byId(gym.fragranceId), ...wearCardStats(gym.count) })
  }

  const complimentTotals = new Map()
  logs.forEach(log => {
    const n = complimentCountForLog(log)
    if (!log.fragrance_id || n <= 0 || isGymWear(log)) return
    complimentTotals.set(log.fragrance_id, (complimentTotals.get(log.fragrance_id) || 0) + n)
  })
  let mostComplimented = null
  for (const [fragranceId, count] of complimentTotals) {
    if (!mostComplimented || count > mostComplimented.count) mostComplimented = { fragranceId, count }
  }
  if (mostComplimented) {
    tiles.push({ key: 'most_complimented', label: 'Most Complimented', fragrance: byId(mostComplimented.fragranceId), ...complimentCardStats(mostComplimented.count) })
  }

  // Flag-off keeps the legacy count/Gym exclusion exactly. Flag-on supplies a
  // duration ranking whose threshold is tag-neutral: a sustained Gym wear can
  // win Most Worn while any brief selection is omitted.
  const durationWinner = Array.isArray(durationRanking) ? durationRanking[0] : null
  const overall = durationRanking === null ? topFragranceByWearCount(logs, log => !isGymWear(log)) : null
  if (durationWinner) {
    tiles.push({ key: 'most_worn', label: 'Most Worn', fragrance: byId(durationWinner.fragrance_id), ...durationCardStats(durationWinner.total_duration_seconds) })
  } else if (overall) {
    tiles.push({ key: 'most_worn', label: 'Most Worn', fragrance: byId(overall.fragranceId), ...wearCardStats(overall.count) })
  }

  const season = currentSeason(new Date())
  const seasonal = topFragranceByWearCount(logs, log => {
    if (isGymWear(log)) return false
    const fragrance = byId(log.fragrance_id)
    return arrayValue(fragrance?.seasons).map(s => s.toLowerCase()).includes(season.toLowerCase())
  })
  if (seasonal) {
    tiles.push({ key: 'go_to_seasonal', label: `Go-To (${season})`, fragrance: byId(seasonal.fragranceId), ...wearCardStats(seasonal.count) })
  }

  // Gym has its own Home treatment. Letting a Gym wear also win the generic
  // Recently Worn tile repeats the same bottle in two different sections.
  const mostRecent = logs.filter(log => !isGymWear(log))
    .sort((a, b) => timestampForLog(b) - timestampForLog(a))[0]
  if (mostRecent?.fragrance_id) {
    tiles.push({ key: 'recently_worn', label: 'Recently Worn', fragrance: byId(mostRecent.fragrance_id), ...lastWornCardStats(relativeDayLabel(mostRecent)) })
  }

  return tiles.filter(tile => tile.fragrance)
}
